import React, { useState, useRef, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, AddressElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { X, CreditCard, ShieldCheck } from 'lucide-react';
import { creationRejectionMessage, joinRejectionMessage } from '../config/sharedTransferRules';
import { supabase } from '../config/supabase';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const PaymentForm = ({ tripId, participantId, amount, onSuccess, onError, onCancel, onSubmittingChange, isWithdrawalFee = false, userProfile, setUserProfile, user, isPrePayment = false, paymentIntentId, immediateCharge = false, canClose = true }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isPaymentComplete, setIsPaymentComplete] = useState(false);
  const [isAddressComplete, setIsAddressComplete] = useState(false);

  useEffect(() => {
    onSubmittingChange?.(loading);
  }, [loading, onSubmittingChange]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setError(null);

    try {
      const userEmail = user?.email;

      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/success`,
          payment_method_data: {
            billing_details: {
              name: userProfile?.name?.trim() || undefined,
              email: userEmail,
              phone: userProfile?.phone?.trim() || undefined
            }
          }
        },
        redirect: 'if_required',
      });

      if (confirmError) throw new Error(confirmError.message);

      await onSuccess({
        success: true,
        payment_completed: true,
        paymentIntentId: paymentIntent?.id || paymentIntentId,
        isPrePayment: isPrePayment || false
      });
    } catch (err) {
      setError(err.message);
      onError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Billing Address */}
      <div className="bg-stone-50 p-5 rounded-2xl border border-stone-100">
        <h3 className="text-sm font-bold text-[#2D1E17] mb-3">Billing Address</h3>
        <AddressElement
          options={{
            mode: 'billing',
            autocomplete: { mode: 'automatic' },
            allowedCountries: [
              'US', 'CA', 'GB', 'AU', 'KE', 'TZ', 'UG', 'RW',
              'DE', 'FR', 'NL', 'ES', 'IT', 'IN', 'JP', 'SG', 'MY',
              'BR', 'MX', 'AR', 'ZA', 'NG', 'EG', 'AE', 'SA', 'NZ', 'PH', 'TH'
            ],
            fields: { phone: 'always' },
            validation: { phone: { required: 'never' } },
          }}
          onChange={(e) => setIsAddressComplete(!!e.complete)}
        />
      </div>

      {/* Payment */}
      <div className="bg-stone-50 p-5 rounded-2xl border border-stone-100">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-[#2D1E17]">Payment Details</h3>
          <span className="text-lg font-bold font-serif text-[#A44432]">${amount}</span>
        </div>
        <p className="text-xs text-stone-500 mb-4">
          {isPrePayment
            ? 'We will securely save your payment method. No charges will be made until your trip is confirmed.'
            : isWithdrawalFee
              ? 'This withdrawal fee will be charged immediately to your card.'
              : immediateCharge
                ? 'Your card will be charged immediately — this trip is already confirmed.'
                : 'Your card will be authorized but not charged until the trip is confirmed.'}
        </p>
        <div className="border border-stone-200 rounded-xl p-3 bg-white">
          <PaymentElement
            options={{
              layout: { type: 'accordion', defaultCollapsed: false, radios: false, spacedAccordionItems: true },
              fields: { billingDetails: { name: 'never', email: 'never', phone: 'never', address: 'never' } }
            }}
            onChange={(e) => setIsPaymentComplete(!!e.complete)}
          />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-100 rounded-xl p-3">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      <div className="flex gap-3">
        <button
          type="submit"
          disabled={!stripe || loading || !isPaymentComplete || (!isPrePayment && !isAddressComplete)}
          className="btn-primary flex-1 py-3"
        >
          {loading ? 'Processing...' :
            isPrePayment ? 'Save Payment Method & Create Trip' :
            isWithdrawalFee ? 'Pay Withdrawal Fee' : 'Authorize Payment'}
        </button>
        <button type="button" onClick={onCancel} disabled={loading || !canClose} className="btn-ghost">Cancel</button>
      </div>
    </form>
  );
};

const StripePaymentModal = ({
  isOpen, onClose, tripId, participantId, amount,
  onSuccess, onError, isWithdrawalFee = false,
  userProfile, setUserProfile, user, isPrePayment = false,
  immediateCharge = false,
  rejectionContext = 'join',
  canClose = true,
  onSubmittingChange,
}) => {
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [isLoadingSecret, setIsLoadingSecret] = useState(true);
  const [initError, setInitError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const prevIsOpenRef = useRef(false);

  const handleSubmittingChange = (submitting) => {
    setIsSubmitting(submitting);
    onSubmittingChange?.(submitting);
  };

  const handleClose = () => {
    if (!canClose || isSubmitting) return;
    onClose();
  };

  useEffect(() => {
    const justOpened = isOpen && !prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;

    if (!isOpen) {
      setClientSecret(null);
      setPaymentIntentId(null);
      setIsLoadingSecret(true);
      setInitError(null);
      setIsSubmitting(false);
      return;
    }

    if (!justOpened || isPrePayment) {
      if (isPrePayment) setIsLoadingSecret(false);
      return;
    }

    setIsLoadingSecret(true);
    setClientSecret(null);
    setPaymentIntentId(null);
    setInitError(null);

    const generateClientSecret = async () => {
      try {
        const session = (await supabase.auth.getSession()).data.session;
        const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/stripe-payment-handler`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token || process.env.REACT_APP_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            action: 'create_payment_intent',
            tripId, participantId, amount,
            userId: user?.id, isWithdrawalFee, userEmail: user?.email,
            customerData: {
              name: userProfile?.name?.trim(),
              email: user?.email,
              phone: userProfile?.phone?.trim()
            }
          })
        });
        const result = await response.json();
        if (response.ok) {
          setClientSecret(result.client_secret);
          setPaymentIntentId(result.payment_intent_id);
        } else {
          const reason = result.error || 'Failed to initialize payment';
          const message = (rejectionContext === 'create'
            ? creationRejectionMessage(reason)
            : joinRejectionMessage(reason)) || reason;
          setInitError(message);
          console.error('Failed to create payment intent:', result.error);
        }
      } catch (error) {
        setInitError(error.message || 'Failed to initialize payment');
        console.error('Error generating client secret:', error);
      } finally {
        setIsLoadingSecret(false);
      }
    };
    generateClientSecret();
  }, [isOpen, tripId, participantId, amount, user?.id, user?.email, isWithdrawalFee, isPrePayment, rejectionContext, userProfile?.name, userProfile?.phone]);

  const elementsOptions = {
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#A44432',
        colorBackground: '#ffffff',
        colorText: '#2D1E17',
        colorDanger: '#dc2626',
        fontFamily: 'Inter, system-ui, sans-serif',
        spacingUnit: '4px',
        borderRadius: '12px',
      },
    },
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div
        className={`absolute inset-0 bg-[#2D1E17]/60 backdrop-blur-sm${canClose ? '' : ' pointer-events-none'}`}
        onClick={handleClose}
      />
      <div className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-stone-100 bg-stone-50 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#FAF3E0] rounded-xl flex items-center justify-center">
              <CreditCard size={20} className="text-[#A44432]" />
            </div>
            <div>
              <h2 className="text-lg font-serif font-bold text-[#2D1E17]">
                {isPrePayment ? 'Setup Payment' : isWithdrawalFee ? 'Withdrawal Fee' : 'Complete Booking'}
              </h2>
              <div className="flex items-center gap-1 text-[10px] text-[#2D5A27] font-bold">
                <ShieldCheck size={12} /> Secure & Encrypted
              </div>
            </div>
          </div>
          <button onClick={handleClose} disabled={!canClose || isSubmitting} className="p-2 hover:bg-stone-200 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            <X size={18} />
          </button>
        </div>

        <div className="p-6">
          {isLoadingSecret ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#A44432]" />
            </div>
          ) : clientSecret ? (
            <Elements stripe={stripePromise} options={elementsOptions}>
              <PaymentForm
                tripId={tripId} participantId={participantId} amount={amount}
                onSuccess={onSuccess} onError={onError} onCancel={handleClose}
                onSubmittingChange={handleSubmittingChange}
                isWithdrawalFee={isWithdrawalFee}
                userProfile={userProfile} setUserProfile={setUserProfile}
                user={user} isPrePayment={isPrePayment}
                paymentIntentId={paymentIntentId}
                immediateCharge={immediateCharge}
                canClose={canClose}
              />
            </Elements>
          ) : (
            <div className="text-center py-12">
              <p className="text-red-600 mb-4">{initError || 'Failed to initialize payment form. Please try again.'}</p>
              <button onClick={handleClose} disabled={!canClose || isSubmitting} className="btn-ghost">Close</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StripePaymentModal;
